import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * StandardPage - Bulletproof page-level loading state wrapper
 *
 * Enforces correct loading order automatically:
 * 1. Loading → Show skeleton or default loading (after threshold)
 * 2. Error → Show error message with optional retry
 * 3. Not Found → Show not found message
 * 4. Content → Render children
 *
 * Skeleton threshold: the skeleton is only shown if `loading` is still true
 * after `skeletonDelayMs` (default 200ms). Fast loads never flash the skeleton.
 * This mirrors the pattern in LoadingOrchestrator.
 *
 * @example
 * <StandardPage
 *   loading={loading}
 *   error={error}
 *   notFound={!data}
 *   skeleton={<MyPageSkeleton />}
 * >
 *   <MyContent data={data} />
 * </StandardPage>
 */
const StandardPage = ({
  loading,
  error,
  notFound,
  skeleton,
  notFoundMessage = 'Not found',
  notFoundDescription = 'The item you are looking for does not exist or has been removed.',
  notFoundActions,
  errorMessage = 'Something went wrong',
  onRetry,
  children,
  className = '',
  skeletonDelayMs = 200,
}) => {
  // Threshold — only show skeleton if loading persists beyond skeletonDelayMs.
  // Prevents skeleton flash on cached / fast loads.
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => setShowSkeleton(true), skeletonDelayMs);
      return () => clearTimeout(timer);
    }
    setShowSkeleton(false);
  }, [loading, skeletonDelayMs]);

  // Step 1: Check error FIRST (higher priority than loading)
  if (error) {
    const errorText = typeof error === 'string' ? error : error?.message || errorMessage;

    return (
      <div className={`container mx-auto p-6 ${className}`}>
        <div className="alert alert-error shadow-lg">
          <div className="flex items-start gap-4 w-full">
            <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-bold text-lg mb-1">Error</h3>
              <p className="text-sm opacity-90">{errorText}</p>
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="btn btn-sm btn-outline"
              >
                Retry
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Step 2: Check not found (before loading animation)
  if (notFound) {
    return (
      <div className={`container mx-auto p-6 ${className}`}>
        <div className="card bg-base-100 shadow-sm rounded-2xl">
          <div className="card-body text-center py-12">
            <div className="text-6xl mb-4">🔍</div>
            <h2 className="text-2xl font-semibold mb-2">{notFoundMessage}</h2>
            <p className="text-base-content/70 mb-6">{notFoundDescription}</p>
            {notFoundActions && (
              <div className="flex justify-center gap-3">
                {notFoundActions}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Animated loading → content transition
  const defaultSkeleton = (
    <div className={`container mx-auto p-6 ${className}`}>
      <div className="card bg-base-100 shadow-sm rounded-2xl p-8">
        <div className="space-y-4">
          <div className="h-8 bg-base-300 rounded skeleton animate-pulse w-1/3"></div>
          <div className="h-4 bg-base-300 rounded skeleton animate-pulse w-2/3"></div>
          <div className="h-4 bg-base-300 rounded skeleton animate-pulse w-1/2"></div>
          <div className="h-32 bg-base-300 rounded skeleton animate-pulse mt-6"></div>
        </div>
      </div>
    </div>
  );

  // Only consider the skeleton branch once the delay has elapsed.
  const showingSkeleton = loading && showSkeleton;

  return (
    <div className="grid grid-cols-1">
      <AnimatePresence>
        {showingSkeleton && (
          <motion.div
            key="skeleton"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ gridArea: '1/1' }}
          >
            {skeleton || defaultSkeleton}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {!loading && (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ gridArea: '1/1' }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

StandardPage.propTypes = {
  loading: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Error),
    PropTypes.object
  ]),
  notFound: PropTypes.bool,
  skeleton: PropTypes.node,
  notFoundMessage: PropTypes.string,
  notFoundDescription: PropTypes.string,
  notFoundActions: PropTypes.node,
  errorMessage: PropTypes.string,
  onRetry: PropTypes.func,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  skeletonDelayMs: PropTypes.number,
};

export default StandardPage;
